import React, { useEffect, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBath,
  faBed,
  faCopy,
  faDoorOpen,
  faExternalLinkAlt,
  faUserGroup
} from '@fortawesome/free-solid-svg-icons';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import CustomCard from '../../components/CustomCard';
import GalleryGrid from '../../components/lakeview/GalleryGrid';
import AmenitiesPreview from '../../components/lakeview/AmenitiesPreview';
import { Actions } from '../../../client/Actions';
import GroupedPreviewList from '../../components/dictionary/GroupedPreviewList';
import renderUtils from '../../helpers/renderUtils';

const HouseDetails = ({ house, width }) => {
  if (!house) return null;

  const [reservations, setReservations] = useState();

  const copyToClipboard = async (value) => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      toast.success('Skopiowano do schowka');
    } catch (e) {
      toast.error('Nie udało się skopiować linku');
    }
  };

  function loadReservations() {
    Actions.get('reservation/house/' + house.id, setReservations);
  }

  useEffect(() => {
    loadReservations();
  }, [house]);

  return (
    <Col md={width}>
      <GalleryGrid mainImage={house.mainImage} images={house.images} />
      <CustomCard
        withoutHeader={true}
        width={12}
        shadow={false}
        className="mb-4 mt-4"
      >
        <Card.Title as="h2" className="h4 fw-bold">
          {house.name}
        </Card.Title>
        <div className="d-flex align-items-center mb-3"></div>
        <Row className="text-muted ">
          <Col>
            <FontAwesomeIcon icon={faUserGroup} /> {house.guestsCount} gości
          </Col>
          <Col>
            <FontAwesomeIcon icon={faBed} /> {house.bedsCount} łóżek
          </Col>
          <Col>
            <FontAwesomeIcon icon={faBath} /> {house.bathroomCount} łazienek
          </Col>
          <Col>
            <FontAwesomeIcon icon={faDoorOpen} /> {house.bedroomsCount} sypialni
          </Col>
        </Row>
      </CustomCard>

      <CustomCard
        withoutHeader={true}
        width={12}
        shadow={false}
        className="mb-4 "
      >
        <Card.Title className="h5 fw-bold">Informacje o domku</Card.Title>
        <hr className="my-2" />
        <Card.Text className="text-muted my-4">
          <div className="white-space">
          {house.description || 'Opis domu nie został jeszcze dodany.'}
          </div>
        </Card.Text>
      </CustomCard>

      <GroupedPreviewList
        title="Zasady pobytu"
        description="Obowiązujące zasady dla tego obiektu"
        api="rule"
        selectedIds={house.rules}
        singleGroup={true}
        previewCount={100}
      />
      <GroupedPreviewList
        title="Udogodnienia"
        description="Informacje o udogodnieniach i usługach w obiekcie"
        api={'amenity/group'}
        selectedIds={house.amenities}
      />

      {house.checkinInstruction && (
        <CustomCard
          withoutHeader={true}
          width={12}
          shadow={false}
          className="mb-4 "
        >
          <Card.Title className="h5 fw-bold">
            Instrukcja zameldowania
          </Card.Title>
          <hr className="my-2" />
          <Card.Text className="text-muted my-4">
            <div className="white-space">
              {house.checkinInstruction ||
                'Instrukcja zameldowania nie została jeszcze dodana'}
            </div>
          </Card.Text>
        </CustomCard>
      )}

      {house.googleMapsUrl && (
        <CustomCard
          withoutHeader={true}
          width={12}
          shadow={false}
          className="mb-4 "
        >
          <Card.Title className="h5 fw-bold">Lokalizacja</Card.Title>
          <hr className="my-2" />

          <div style={{ borderRadius: '8px', overflow: 'hidden' }}>
            <iframe
              title="Google Maps"
              src={house.googleMapsUrl}
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </CustomCard>
      )}
      {(house.bookingCalendarUrl || house.lastBookingSync || house.reservationCalendarExportUrl) && (
        <CustomCard
          withoutHeader={true}
          width={12}
          shadow={false}
          className="mb-4 "
        >
          <Card.Title className="h5 fw-bold">Integracje</Card.Title>
          <hr className="my-2" />
          <div className="text-muted my-4">
            {house.bookingCalendarUrl && (
              <div className="mb-3">
                <strong>URL kalendarza Booking.com:</strong>{' '}
                <a
                  href={house.bookingCalendarUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {house.bookingCalendarUrl}
                </a>
              </div>
            )}
            {house.reservationCalendarExportUrl && (
              <div className="mb-3">
                <strong>Eksport kalendarza rezerwacji:</strong>{' '}
                <div className="mt-2">
                  <a
                    href={house.reservationCalendarExportUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="d-inline-block mb-2"
                  >
                    {house.reservationCalendarExportUrl}
                  </a>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        copyToClipboard(house.reservationCalendarExportUrl)
                      }
                    >
                      <FontAwesomeIcon icon={faCopy} className="me-2" />
                      Kopiuj
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        window.open(
                          house.reservationCalendarExportUrl,
                          '_blank',
                        )
                      }
                    >
                      <FontAwesomeIcon
                        icon={faExternalLinkAlt}
                        className="me-2"
                      />
                      Otwórz
                    </Button>
                  </div>
                  <div className="small text-muted mt-2">
                    Eksport zawiera rezerwacje i ręczne blokady dni. Blokady z
                    importu Booking.com nie są uwzględniane.
                  </div>
                </div>
              </div>
            )}
            <div>
              <strong>Ostatnia synchronizacja Booking.com:</strong>{' '}
              {house.lastBookingSync
                ? renderUtils.formatDateWithTime(house.lastBookingSync)
                : 'Brak'}
            </div>
          </div>
        </CustomCard>
      )}
      {/*{reservations && (*/}
      {/*  <CustomCard*/}
      {/*    withoutHeader={true}*/}
      {/*    width={12}*/}
      {/*    shadow={false}*/}
      {/*    className="mb-4 "*/}
      {/*  >*/}
      {/*    <Card.Title className="h5 fw-bold">Kalendarz</Card.Title>*/}
      {/*    <hr className="my-2" />*/}
      {/*    <Card.Text className="text-muted my-4">*/}
      {/*      <BigCalendar views={['month']} reservations={reservations} />*/}
      {/*    </Card.Text>*/}
      {/*  </CustomCard>*/}
      {/*)}*/}
    </Col>
  );
};

export default HouseDetails;
